import React, { useState, useEffect } from "react";
import axios from "axios";
import { Search, ChevronRight, X, Plus, Minus, QrCode, Receipt, Loader2, Check, Trash2 } from "lucide-react";
import QrScanner from "../../components/QrScanner";
import WhatsAppChat from "../../components/WhatsAppChat";
import { useAuth } from "../../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const VIP_COLORS = { Diamond: "#B9F2FF", Platinum: "#E8E8E8", Gold: "#C5A059", Silver: "#C0C0C0", Standard: "#888" };
const TIER_CASHBACK = { Diamond: 4, Platinum: 3, Gold: 2, Silver: 1, Standard: 1 };
const CRM_STATUS_LABELS = { new: "Новый", contacted: "В работе", thinking: "Думает", appointment: "Примерка", purchased: "Куплено", lost: "Отказ" };
const CRM_STATUS_COLORS = { new: "#3b82f6", contacted: "#8b5cf6", thinking: "#f59e0b", appointment: "#06b6d4", purchased: "#16a34a", lost: "#94a3b8" };

// Source of a client record — where it originally came from. Badges are shown
// in the list so a manager can tell at a glance who is real vs. QA junk.
const SOURCE_META = {
  imported:    { label: "Импорт",   bg: "#EEF2FF", fg: "#4338CA", border: "#C7D2FE" },
  manual:      { label: "Менеджер", bg: "#ECFDF5", fg: "#047857", border: "#A7F3D0" },
  self_signup: { label: "Регистр.", bg: "#FFFBEB", fg: "#B45309", border: "#FDE68A" },
  whatsapp:    { label: "WhatsApp", bg: "#DCFCE7", fg: "#166534", border: "#86EFAC" },
  whatsapp_click: { label: "WhatsApp", bg: "#DCFCE7", fg: "#166534", border: "#86EFAC" },
  test:        { label: "ТЕСТ",     bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA" },
};

function ClientDetail({ client: initialClient, onClose, onDeleted }) {
  const { user } = useAuth();
  const [client, setClient] = useState(initialClient);
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    const r = await axios.get(`${API}/admin/clients/${client.id}`, { withCredentials: true });
    setClient(r.data);
  };

  const removeClient = async () => {
    if (!window.confirm(
      `Удалить клиента «${client.name || client.phone}»?\n\nБудут стёрты: карточка клиента, история покупок, все бонусные транзакции, задачи CRM, записи на примерку, редеемции. Действие необратимо.`
    )) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/clients/${client.id}`, { withCredentials: true });
      onDeleted && onDeleted();
      onClose();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Ошибка удаления");
    } finally { setBusy(false); }
  };

  // Toggle the "test" flag — flips the badge and enables/excludes the client
  // from the "Purge test" bulk action. Un-flagging restores the derived
  // source (imported / manual / self_signup) so the badge stays honest.
  const toggleTestFlag = async () => {
    const next = !client.is_test;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/clients/${client.id}/mark-test`,
        { is_test: next },
        { withCredentials: true },
      );
      setClient({ ...client, is_test: r.data.is_test, client_source: r.data.client_source });
      onDeleted && onDeleted(); // reuse the parent refresh so the list badge updates
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Не удалось обновить метку");
    } finally { setBusy(false); }
  };

  const color = VIP_COLORS[client.vip_level] || "#888";
  const isAdmin = user?.role === "admin";
  const isSenior = ["admin", "senior_manager"].includes(user?.role);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
      <div style={{ background: "white", borderRadius: "12px", width: "100%", maxWidth: "560px", maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", fontWeight: 400 }}>{client.name}</h3>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {isAdmin && (
              <button data-testid="toggle-test-flag-btn" onClick={toggleTestFlag} disabled={busy}
                      title={client.is_test ? "Снять метку «ТЕСТ»" : "Отметить как тестового клиента"}
                      style={{ background: client.is_test ? "#FEF2F2" : "white",
                                color: client.is_test ? "#B91C1C" : "#6b7280",
                                border: `1px solid ${client.is_test ? "#FECACA" : "#e5e7eb"}`,
                                borderRadius: "6px", padding: "6px 10px", cursor: busy ? "wait" : "pointer",
                                fontFamily: "Manrope", fontSize: "11px", fontWeight: 600, letterSpacing: "0.06em",
                                textTransform: "uppercase" }}>
                {client.is_test ? "Тест ✓" : "Отметить тест"}
              </button>
            )}
            {isAdmin && (
              <button data-testid="delete-client-btn" onClick={removeClient} disabled={busy}
                      title="Удалить клиента (только админ)"
                      style={{ background: "white", color: "#dc2626", border: "1px solid #fecaca",
                                borderRadius: "6px", padding: "6px 10px", cursor: busy ? "wait" : "pointer",
                                fontFamily: "Manrope", fontSize: "11px", fontWeight: 600, letterSpacing: "0.06em",
                                textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                <Trash2 size={12} /> {busy ? "…" : "Удалить"}
              </button>
            )}
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}><X size={20} /></button>
          </div>
        </div>
        <div style={{ padding: "20px 24px" }}>
          {/* Info */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "20px" }}>
            {[["Телефон", client.phone], ["Город", client.city || "—"], ["VIP статус", client.vip_level], ["Бонусы доступно", `${Math.floor(client.bonus_balance || 0).toLocaleString("ru-RU")} ₸`], ["Заморожено", `${Math.floor(client.locked_bonus || 0).toLocaleString("ru-RU")} ₸`], ["Покупок", `${(client.total_purchases || 0).toLocaleString("ru-RU")} ₸`]].map(([l, v]) => (
              <div key={l} style={{ background: "#f9fafb", borderRadius: "6px", padding: "12px" }}>
                <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "#888", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "4px" }}>{l}</div>
                <div style={{ fontFamily: "Manrope", fontSize: "14px", color: l === "VIP статус" ? color : "#0A0A0A", fontWeight: l === "VIP статус" ? 700 : 400 }}>{v}</div>
              </div>
            ))}
          </div>

          {/* Add Purchase + optional bonus redemption */}
          <PurchaseFlow client={client} onCompleted={refresh} />

          {/* Manual bonus adjustment — admin & senior_manager only */}
          {isSenior && <BonusAdjust client={client} onDone={refresh} />}

          {/* WhatsApp chat — read & reply without leaving the client card */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ fontFamily: "Manrope", fontSize: "12px", fontWeight: 700,
                          color: "#0A0A0A", letterSpacing: "0.08em",
                          textTransform: "uppercase", marginBottom: "10px" }}>
              WhatsApp
            </div>
            <WhatsAppChat clientId={client.id} client={client} />
          </div>

          {/* Recent Purchases */}
          {client.purchases?.length > 0 && (
            <div style={{ marginBottom: "20px" }}>
              <div style={{ fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, color: "#0A0A0A", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "10px" }}>История покупок</div>
              {client.purchases.slice(0, 5).map(p => (
                <div key={p.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "#374151" }}>{p.description}</div>
                  <div style={{ fontFamily: "Manrope", fontSize: "13px", fontWeight: 600, color: "#C5A059" }}>{(p.amount).toLocaleString("ru-RU")} ₸</div>
                </div>
              ))}
            </div>
          )}

          {/* Bonus history */}
          {client.bonus_history?.length > 0 && (
            <div>
              <div style={{ fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, color: "#0A0A0A", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "10px" }}>История бонусов</div>
              {client.bonus_history.slice(0, 8).map(t => {
                const positive = (t.amount || 0) > 0;
                const labels = { welcome: "Приветственный", cashback: "Кэшбэк", tier_cashback: "Кэшбэк", purchase_cashback: "Кэшбэк", referral_inviter: "Реферал", referral_invitee: "Бонус другу", referral_signup: "Реферал", referral_purchase: "Кэшбэк с друга", birthday: "День рождения", spend: "Списание", spent: "Списание", expired: "Истёк", manual_add: "Начислено вручную", admin_adjust: "Корректировка админа", admin_grant: "Начислено админом", admin_deduct: "Списано админом" };
                const label = labels[t.type] || t.type || "Бонус";
                return (
                  <div key={t.id} data-testid={`admin-bonus-tx-${t.id}`} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1f5f9", alignItems: "center" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "#374151" }}>{label}{t.description ? ` · ${t.description}` : ""}</div>
                      <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "#9ca3af", marginTop: "2px" }}>
                        {t.created_at ? new Date(t.created_at).toLocaleString("ru-RU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                        {t.unlock_at && new Date(t.unlock_at) > new Date() && (
                          <span style={{ marginLeft: "8px", color: "#9ca3af" }}>· разблок. {new Date(t.unlock_at).toLocaleDateString("ru-RU", { day: "numeric", month: "short" })}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ fontFamily: "Manrope", fontSize: "13px", fontWeight: 700, color: positive ? "#C5A059" : "#ef4444", whiteSpace: "nowrap" }}>
                      {positive ? "+" : ""}{Math.floor(t.amount || 0).toLocaleString("ru-RU")}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BonusAdjust({ client, onDone }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // {ok, text}

  const run = async (op) => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setMsg({ ok: false, text: "Укажите сумму больше нуля" }); return; }
    if (op === "deduct" && amt > (client.bonus_balance || 0)) {
      setMsg({ ok: false, text: `Доступно только ${Math.floor(client.bonus_balance || 0).toLocaleString("ru-RU")}` });
      return;
    }
    const verb = op === "add" ? "НАЧИСЛИТЬ" : "СПИСАТЬ";
    if (!window.confirm(`${verb} ${amt.toLocaleString("ru-RU")} бонусов клиенту «${client.name || client.phone}»?${desc ? `\nКомментарий: ${desc}` : ""}`)) return;
    setBusy(true); setMsg(null);
    try {
      const r = await axios.post(`${API}/admin/bonus/${op}`,
        { client_id: client.id, amount: amt, description: desc.trim() },
        { withCredentials: true });
      setMsg({ ok: true, text: r.data.message });
      setAmount(""); setDesc("");
      onDone && onDone();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Ошибка операции" });
    }
    setBusy(false);
  };

  return (
    <div data-testid="bonus-adjust-block" style={{ background: "#fffbf5", border: "1px solid #f0e7d8", borderRadius: "8px", padding: "14px 16px", marginBottom: "16px" }}>
      <button data-testid="bonus-adjust-toggle" onClick={() => setOpen(!open)}
        style={{ background: "none", border: "none", cursor: "pointer", padding: 0, width: "100%",
                 display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, color: "#8a6d2a", letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Корректировка бонусов (админ / ст. менеджер)
        </span>
        <span style={{ color: "#8a6d2a", fontSize: "14px" }}>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div style={{ marginTop: "12px" }}>
          <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
            <input data-testid="bonus-adjust-amount" type="number" min="1" value={amount}
              onChange={e => setAmount(e.target.value)} placeholder="Сумма бонусов"
              style={{ flex: 1, background: "white", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "9px 12px", fontFamily: "Manrope", fontSize: "13px", outline: "none" }} />
            <input data-testid="bonus-adjust-desc" value={desc}
              onChange={e => setDesc(e.target.value)} placeholder="Причина (напр. договорённость)"
              style={{ flex: 2, background: "white", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "9px 12px", fontFamily: "Manrope", fontSize: "13px", outline: "none" }} />
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button data-testid="bonus-adjust-add-btn" onClick={() => run("add")} disabled={busy}
              style={{ flex: 1, background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0", borderRadius: "5px", padding: "9px", cursor: busy ? "wait" : "pointer", fontFamily: "Manrope", fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              + Начислить
            </button>
            <button data-testid="bonus-adjust-deduct-btn" onClick={() => run("deduct")} disabled={busy}
              style={{ flex: 1, background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FECACA", borderRadius: "5px", padding: "9px", cursor: busy ? "wait" : "pointer", fontFamily: "Manrope", fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              − Списать
            </button>
          </div>
          {msg && (
            <div data-testid="bonus-adjust-msg" style={{ marginTop: "8px", fontFamily: "Manrope", fontSize: "12px", color: msg.ok ? "#047857" : "#DC2626" }}>
              {msg.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PurchaseFlow({ client, onCompleted }) {
  const { user } = useAuth();
  const isSenior = ["admin", "senior_manager"].includes(user?.role);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [useBonuses, setUseBonuses] = useState(false);
  const [bonusAmount, setBonusAmount] = useState("");
  // Lifecycle: idle → requesting → waiting → confirmed → completing → receipt
  const [stage, setStage] = useState("idle");
  const [redemptionId, setRedemptionId] = useState(null);
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState(null);

  const tierPct = TIER_CASHBACK[client.vip_level] || 1;
  const purchaseAmt = parseFloat(amount) || 0;
  const bonusAmt = parseFloat(bonusAmount) || 0;
  const maxRedemption = Math.floor(purchaseAmt * 0.2);
  const availableBonus = client.bonus_balance || 0;
  const cappedMaxRedeem = Math.min(maxRedemption, availableBonus);
  const projectedCashback = Math.round(purchaseAmt * tierPct / 100);
  const amountToPay = useBonuses ? Math.max(0, purchaseAmt - bonusAmt) : purchaseAmt;

  // Validation
  const overCap = useBonuses && bonusAmt > maxRedemption;
  let validationErr = "";
  if (purchaseAmt <= 0) validationErr = "";
  else if (useBonuses && bonusAmt <= 0) validationErr = "Укажите сумму к списанию";
  else if (overCap && !isSenior) validationErr = `Максимум 20% от чека: ${maxRedemption.toLocaleString("ru-RU")} ₸`;
  else if (useBonuses && bonusAmt > availableBonus) validationErr = `Недостаточно бонусов. Доступно: ${Math.floor(availableBonus).toLocaleString("ru-RU")}`;

  const requestRedemption = async () => {
    setError(""); setStage("requesting");
    try {
      const r = await axios.post(`${API}/admin/redemptions/request`, {
        client_id: client.id,
        purchase_amount: purchaseAmt,
        bonus_amount: bonusAmt,
        description: description || `Покупка ${purchaseAmt.toLocaleString("ru-RU")} ₸`,
        override_cap: overCap && isSenior,
      }, { withCredentials: true });
      setRedemptionId(r.data.id);
      setStage("waiting");
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось создать запрос");
      setStage("idle");
    }
  };

  // Poll status while waiting
  useEffect(() => {
    if (stage !== "waiting" || !redemptionId) return;
    const interval = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/admin/redemptions/${redemptionId}`, { withCredentials: true });
        if (r.data.status === "confirmed") { setStage("confirmed"); clearInterval(interval); }
        else if (r.data.status === "declined") { setError("Клиент отклонил списание"); setStage("idle"); setRedemptionId(null); clearInterval(interval); }
        else if (r.data.status === "cancelled" || r.data.status === "superseded") { setStage("idle"); setRedemptionId(null); clearInterval(interval); }
      } catch { /* keep polling */ }
    }, 2000);
    return () => clearInterval(interval);
  }, [stage, redemptionId]);

  const cancelRequest = async () => {
    if (!redemptionId) { setStage("idle"); return; }
    try { await axios.post(`${API}/admin/redemptions/${redemptionId}/cancel`, {}, { withCredentials: true }); } catch { /* ignore */ }
    setStage("idle"); setRedemptionId(null);
  };

  const completePurchase = async () => {
    setError(""); setStage("completing");
    try {
      const payload = {
        client_id: client.id, amount: purchaseAmt,
        description: description || `Покупка ${purchaseAmt.toLocaleString("ru-RU")} ₸`,
      };
      if (useBonuses && redemptionId) payload.redemption_id = redemptionId;
      const r = await axios.post(`${API}/admin/purchases`, payload, { withCredentials: true });
      setReceipt(r.data);
      setStage("receipt");
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось завершить покупку");
      setStage(useBonuses ? "confirmed" : "idle");
    }
  };

  const reset = () => {
    setAmount(""); setDescription(""); setUseBonuses(false); setBonusAmount("");
    setStage("idle"); setRedemptionId(null); setReceipt(null); setError("");
    if (onCompleted) onCompleted();
  };

  // RECEIPT screen
  if (stage === "receipt" && receipt) {
    return (
      <div data-testid="purchase-receipt" style={{ background: "linear-gradient(135deg,#FDFBF7,#fff)", border: "1px solid #C5A059", borderRadius: "10px", padding: "20px", marginBottom: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
          <Receipt size={20} style={{ color: "#C5A059" }} />
          <div style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", color: "#0A0A0A" }}>Чек операции</div>
        </div>
        <Row label="Покупка" value={`${receipt.purchase_amount.toLocaleString("ru-RU")} ₸`} />
        {receipt.bonus_redeemed > 0 && <Row label="Списано бонусов" value={`${receipt.bonus_redeemed.toLocaleString("ru-RU")} ₸`} color="#E11D2A" />}
        <Row label="Оплачено клиентом" value={`${receipt.amount_to_pay.toLocaleString("ru-RU")} ₸`} bold />
        <div style={{ height: "1px", background: "#e5e7eb", margin: "10px 0" }} />
        <Row label={`Начислено бонусов (${receipt.cashback_percent}% ${receipt.cashback_level})`} value={`+${receipt.bonus_earned.toLocaleString("ru-RU")}`} color="#C5A059" bold />
        <Row label="Новый баланс" value={`${Math.floor(receipt.new_bonus_balance).toLocaleString("ru-RU")} бонусов`} />
        {receipt.vip_upgraded && (
          <div style={{ marginTop: "12px", padding: "10px 12px", background: "linear-gradient(135deg,#FEF3C7,#FDE68A)", borderRadius: "6px", fontFamily: "Manrope", fontSize: "12px", color: "#78350F", fontWeight: 600 }}>
            🎉 Уровень повышен до {receipt.new_vip_level}! Следующий кэшбэк {TIER_CASHBACK[receipt.new_vip_level] || 1}%.
          </div>
        )}
        <button data-testid="receipt-close-btn" onClick={reset}
          style={{ marginTop: "14px", width: "100%", background: "#0A0A0A", color: "white", border: "none", borderRadius: "5px", padding: "10px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Готово
        </button>
      </div>
    );
  }

  // WAITING for client confirmation
  if (stage === "waiting") {
    return (
      <div data-testid="purchase-waiting" style={{ background: "#FFFBEB", border: "1px solid #F59E0B", borderRadius: "10px", padding: "18px", marginBottom: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
          <Loader2 size={18} style={{ color: "#F59E0B", animation: "spin 1s linear infinite" }} />
          <div style={{ fontFamily: "Manrope", fontSize: "13px", fontWeight: 700, color: "#78350F" }}>Ожидание подтверждения клиента</div>
        </div>
        <div style={{ fontFamily: "Manrope", fontSize: "12.5px", color: "#92400E", lineHeight: 1.55, marginBottom: "12px" }}>
          Попросите клиента <b>{client.name}</b> открыть приложение «Angiolini VIP Club» — там появится запрос на списание <b>{bonusAmt.toLocaleString("ru-RU")} ₸</b> за покупку <b>{purchaseAmt.toLocaleString("ru-RU")} ₸</b>. После нажатия «Подтвердить» эта форма обновится автоматически.
        </div>
        <button data-testid="cancel-redemption-btn" onClick={cancelRequest}
          style={{ background: "white", border: "1px solid #FCA5A5", color: "#DC2626", borderRadius: "5px", padding: "8px 14px", cursor: "pointer", fontFamily: "Manrope", fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Отменить и оплатить без бонусов
        </button>
      </div>
    );
  }

  // CONFIRMED — ready to complete
  if (stage === "confirmed") {
    return (
      <div data-testid="purchase-confirmed" style={{ background: "#ECFDF5", border: "1px solid #10B981", borderRadius: "10px", padding: "18px", marginBottom: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
          <Check size={18} style={{ color: "#10B981" }} />
          <div style={{ fontFamily: "Manrope", fontSize: "13px", fontWeight: 700, color: "#065F46" }}>Клиент подтвердил списание</div>
        </div>
        <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "#047857", lineHeight: 1.5, marginBottom: "12px" }}>
          К оплате: <b>{amountToPay.toLocaleString("ru-RU")} ₸</b> (покупка {purchaseAmt.toLocaleString("ru-RU")} ₸ − {bonusAmt.toLocaleString("ru-RU")} бонусов)
        </div>
        {error && <div style={{ marginBottom: "10px", color: "#DC2626", fontFamily: "Manrope", fontSize: "12px" }}>{error}</div>}
        <button data-testid="complete-purchase-btn" onClick={completePurchase} disabled={stage === "completing"}
          style={{ width: "100%", background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "5px", padding: "12px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>
          {stage === "completing" ? "Завершаем…" : "Завершить покупку"}
        </button>
      </div>
    );
  }

  // IDLE form
  return (
    <div style={{ background: "#fafafa", borderRadius: "8px", padding: "16px", marginBottom: "16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
        <div style={{ fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, color: "#0A0A0A", letterSpacing: "0.08em", textTransform: "uppercase" }}>Новая покупка</div>
        <div data-testid="cashback-rate-hint" style={{ fontFamily: "Manrope", fontSize: "11px", color: "#C5A059", fontWeight: 700, background: "#C5A05922", border: "1px solid #C5A059", borderRadius: "3px", padding: "3px 8px" }}>
          Кэшбэк {tierPct}% ({client.vip_level})
        </div>
      </div>
      <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
        <input data-testid="purchase-amount" type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Сумма покупки ₸"
          style={{ flex: 1, background: "white", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "10px 12px", fontFamily: "Manrope", fontSize: "14px", outline: "none" }} />
        <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="Описание (напр. Молто Кальдо)"
          style={{ flex: 2, background: "white", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "10px 12px", fontFamily: "Manrope", fontSize: "14px", outline: "none" }} />
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: availableBonus > 0 && purchaseAmt > 0 ? "pointer" : "not-allowed", marginBottom: "10px", opacity: availableBonus > 0 && purchaseAmt > 0 ? 1 : 0.5 }}>
        <input data-testid="use-bonuses-cb" type="checkbox" checked={useBonuses} disabled={availableBonus <= 0 || purchaseAmt <= 0} onChange={e => setUseBonuses(e.target.checked)} style={{ accentColor: "#C5A059", width: "16px", height: "16px" }} />
        <span style={{ fontFamily: "Manrope", fontSize: "13px", color: "#374151" }}>
          Списать бонусы клиента (доступно: <b>{Math.floor(availableBonus).toLocaleString("ru-RU")}</b>, макс. за чек: <b>{cappedMaxRedeem.toLocaleString("ru-RU")}</b>)
        </span>
      </label>

      {useBonuses && (
        <div style={{ background: "white", border: "1px solid #C5A059", borderRadius: "6px", padding: "12px", marginBottom: "10px" }}>
          <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#6b7280", marginBottom: "6px" }}>Сумма к списанию (макс. 20% от чека):</div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input data-testid="bonus-amount" type="number" value={bonusAmount} onChange={e => setBonusAmount(e.target.value)} max={cappedMaxRedeem}
              placeholder={`до ${cappedMaxRedeem.toLocaleString("ru-RU")}`}
              style={{ flex: 1, background: "#fafafa", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "9px 12px", fontFamily: "Manrope", fontSize: "14px", outline: "none" }} />
            <button data-testid="max-bonus-btn" type="button" onClick={() => setBonusAmount(String(cappedMaxRedeem))}
              style={{ background: "#C5A05922", border: "1px solid #C5A059", color: "#C5A059", borderRadius: "4px", padding: "9px 14px", cursor: "pointer", fontFamily: "Manrope", fontSize: "11px", fontWeight: 700 }}>
              Макс
            </button>
          </div>
          {validationErr && <div style={{ marginTop: "6px", fontFamily: "Manrope", fontSize: "12px", color: "#DC2626" }}>{validationErr}</div>}
          {!validationErr && overCap && isSenior && (
            <div data-testid="over-cap-warning" style={{ marginTop: "6px", fontFamily: "Manrope", fontSize: "12px", color: "#B45309", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: "4px", padding: "6px 8px" }}>
              ⚠ Выше лимита 20% ({maxRedemption.toLocaleString("ru-RU")} ₸) — списание по устной договорённости с клиентом. Доступно только админу и ст. менеджеру.
            </div>
          )}
        </div>
      )}

      {purchaseAmt > 0 && (
        <div style={{ background: "#fff", border: "1px dashed #e5e7eb", borderRadius: "6px", padding: "10px 12px", marginBottom: "10px", fontFamily: "Manrope", fontSize: "12px", color: "#6b7280" }}>
          {useBonuses && bonusAmt > 0 && (
            <div>К оплате: <b style={{ color: "#0A0A0A" }}>{amountToPay.toLocaleString("ru-RU")} ₸</b> (минус {bonusAmt.toLocaleString("ru-RU")} бонусов)</div>
          )}
          <div>Будет начислено: <b style={{ color: "#C5A059" }}>+{projectedCashback.toLocaleString("ru-RU")} ₸</b> кэшбэка от полной суммы {purchaseAmt.toLocaleString("ru-RU")} ₸ (15 дней блокировки)</div>
        </div>
      )}

      {error && <div style={{ marginBottom: "10px", color: "#DC2626", fontFamily: "Manrope", fontSize: "12px" }}>{error}</div>}

      {useBonuses ? (
        <button data-testid="request-bonus-btn" onClick={requestRedemption} disabled={!purchaseAmt || !bonusAmt || !!validationErr || stage === "requesting"}
          style={{ width: "100%", background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "5px", padding: "12px", cursor: !purchaseAmt || !bonusAmt || !!validationErr ? "not-allowed" : "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", opacity: !purchaseAmt || !bonusAmt || !!validationErr ? 0.5 : 1 }}>
          {stage === "requesting" ? "Создаём запрос…" : "Запросить подтверждение клиента"}
        </button>
      ) : (
        <button data-testid="add-purchase-btn" onClick={completePurchase} disabled={!purchaseAmt}
          style={{ width: "100%", background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "5px", padding: "12px", cursor: !purchaseAmt ? "not-allowed" : "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", opacity: !purchaseAmt ? 0.5 : 1 }}>
          {stage === "completing" ? "Добавляем…" : "Добавить покупку"}
        </button>
      )}
    </div>
  );
}

function Row({ label, value, color, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontFamily: "Manrope", fontSize: "13.5px", color: color || "#374151", fontWeight: bold ? 700 : 400 }}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

export default function AdminClients() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanError, setScanError] = useState("");
  // Source filter: "" = all, or one of imported/manual/self_signup/test
  const [sourceFilter, setSourceFilter] = useState("");
  const [purging, setPurging] = useState(false);
  // Bulk selection — set of client IDs the manager has ticked. Persists
  // across filter changes so a user can, say, tick a few "self_signup" rows,
  // switch to "test", tick more, then delete both batches at once.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [sortBy, setSortBy] = useState("newest");
  const isAdmin = user?.role === "admin";

  const toggleId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const load = async (q, sortOverride) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("search", q);
      params.set("sort", sortOverride || sortBy);
      const r = await axios.get(`${API}/admin/clients?${params.toString()}`, { withCredentials: true });
      setClients(r.data);
    } catch { /* noop */ } finally { setLoading(false); }
  };

  // eslint-disable-next-line
  useEffect(() => { load(""); }, []);
  useEffect(() => { const t = setTimeout(() => load(search), 400); return () => clearTimeout(t); }, [search]);
  // eslint-disable-next-line
  useEffect(() => { load(search, sortBy); }, [sortBy]);

  const openClient = async (id) => {
    const r = await axios.get(`${API}/admin/clients/${id}`, { withCredentials: true });
    setSelected(r.data);
  };

  const handleScannedClient = async (clientId) => {
    setScannerOpen(false);
    setScanError("");
    try {
      const r = await axios.get(`${API}/admin/clients/${clientId}`, { withCredentials: true });
      setSelected(r.data);
    } catch (e) {
      setScanError(e?.response?.status === 404 ? "Клиент с таким ID не найден" : "Не удалось загрузить клиента");
      setTimeout(() => setScanError(""), 4000);
    }
  };

  // Sweep: 1) backfill client_source for legacy rows AND auto-flag obvious
  // test names (TEST_*, "Test ...", "Friend"), then 2) show the count of
  // items that just got flagged. Nothing is deleted here — that's the
  // separate red-button action right next to it.
  const runAutoDetect = async () => {
    if (!window.confirm("Автоматически найти и пометить как «ТЕСТ» всех клиентов с именами вида TEST_*, «Test *», «Friend»?")) return;
    setPurging(true);
    try {
      const r = await axios.post(`${API}/admin/clients-tools/backfill-source`, {}, { withCredentials: true });
      const flagged = r.data.flagged_test || [];
      window.alert(`Готово. Отмечено новых тестовых: ${flagged.length}`);
      setSourceFilter("test");
      await load(search);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Ошибка автопоиска");
    } finally { setPurging(false); }
  };

  const purgeTest = async () => {
    if (!window.confirm(
      "УДАЛИТЬ ВСЕХ клиентов с меткой «ТЕСТ»?\n\n"
      + "Будут стёрты: карточки, покупки, бонусы, задачи, записи и редеемции этих клиентов. Действие необратимо.\n\n"
      + "Совет: сначала отфильтруйте по «Тест» и проверьте список.",
    )) return;
    setPurging(true);
    try {
      const r = await axios.delete(`${API}/admin/clients-tools/purge-test`, { withCredentials: true });
      window.alert(`Удалено клиентов: ${r.data.deleted_count}`);
      await load(search);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Ошибка удаления");
    } finally { setPurging(false); }
  };

  const filtered = sourceFilter
    ? clients.filter(c => {
        const s = c.client_source || "self_signup";
        // Group whatsapp + whatsapp_click under a single "whatsapp" filter.
        if (sourceFilter === "whatsapp") return s === "whatsapp" || s === "whatsapp_click";
        return s === sourceFilter;
      })
    : clients;
  const counts = clients.reduce((acc, c) => {
    const s = c.client_source || "self_signup";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  // Header checkbox reflects "all visible in current filter are ticked"
  const filteredIds = filtered.map(c => c.id);
  const allVisibleSelected = filteredIds.length > 0 && filteredIds.every(id => selectedIds.has(id));
  const someVisibleSelected = filteredIds.some(id => selectedIds.has(id));
  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) { filteredIds.forEach(id => next.delete(id)); }
      else                    { filteredIds.forEach(id => next.add(id)); }
      return next;
    });
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(
      `УДАЛИТЬ ${ids.length} выбранных клиентов?\n\n`
      + "Будут стёрты: карточки, покупки, бонусы, задачи, записи и редеемции этих клиентов. Действие необратимо.",
    )) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/admin/clients-tools/bulk-delete`, { ids }, { withCredentials: true });
      window.alert(`Удалено клиентов: ${r.data.deleted_count}`);
      clearSelection();
      await load(search);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Ошибка массового удаления");
    } finally { setBulkBusy(false); }
  };

  const bulkMarkTest = async (flag) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/clients-tools/bulk-mark-test`,
        { ids, is_test: flag },
        { withCredentials: true },
      );
      window.alert(`Обновлено клиентов: ${r.data.modified}`);
      clearSelection();
      await load(search);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Ошибка");
    } finally { setBulkBusy(false); }
  };

  const colors = { Diamond: "#B9F2FF", Platinum: "#E8E8E8", Gold: "#C5A059", Silver: "#C0C0C0", Standard: "#888" };

  return (
    <div>
      <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "20px", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: "240px", position: "relative", maxWidth: "400px" }}>
          <Search size={15} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#999" }} />
          <input data-testid="client-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Поиск по имени или телефону..."
            style={{ width: "100%", background: "white", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "11px 14px 11px 36px", fontFamily: "Manrope", fontSize: "14px", outline: "none" }} />
        </div>
        <select data-testid="clients-sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}
          style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "10px 12px",
                   fontFamily: "Manrope", fontSize: "12.5px", cursor: "pointer", outline: "none", color: "#374151" }}>
          <option value="newest">Сначала новые</option>
          <option value="oldest">Сначала старые</option>
          <option value="purchases">По сумме покупок</option>
        </select>
        <button data-testid="scan-qr-btn" onClick={() => setScannerOpen(true)}
          style={{ background: "linear-gradient(135deg,#0A0A0A,#1f1f1f)", color: "#D4B878", border: "1px solid #C5A059", borderRadius: "6px", padding: "10px 18px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: "8px" }}>
          <QrCode size={15} /> Сканировать QR
        </button>
      </div>

      {/* Source filter tabs + admin bulk actions */}
      <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", marginBottom: "16px" }}>
        {[
          { id: "",            label: "Все",         count: clients.length },
          { id: "imported",    label: "Импорт",      count: counts.imported || 0 },
          { id: "manual",      label: "Менеджер",    count: counts.manual || 0 },
          { id: "whatsapp",    label: "WhatsApp",    count: (counts.whatsapp || 0) + (counts.whatsapp_click || 0) },
          { id: "self_signup", label: "Регистрация", count: counts.self_signup || 0 },
          { id: "test",        label: "Тестовые",    count: counts.test || 0 },
        ].map(tab => {
          const active = sourceFilter === tab.id;
          const meta = SOURCE_META[tab.id];
          return (
            <button key={tab.id || "all"} data-testid={`source-filter-${tab.id || "all"}`}
              onClick={() => setSourceFilter(tab.id)}
              style={{
                background: active ? (meta?.bg || "#0A0A0A") : "white",
                color: active ? (meta?.fg || "#D4B878") : "#374151",
                border: `1px solid ${active ? (meta?.border || "#0A0A0A") : "#e5e7eb"}`,
                borderRadius: "20px", padding: "6px 14px", cursor: "pointer",
                fontFamily: "Manrope", fontSize: "12px", fontWeight: active ? 700 : 500,
                letterSpacing: "0.03em",
              }}>
              {tab.label} · {tab.count}
            </button>
          );
        })}

        <div style={{ flex: 1 }} />

        {isAdmin && (
          <>
            <button data-testid="auto-detect-test-btn" onClick={runAutoDetect} disabled={purging}
              title="Проставить источник всем и авто-пометить очевидных тестовых"
              style={{ background: "white", color: "#374151", border: "1px solid #e5e7eb",
                       borderRadius: "6px", padding: "8px 14px", cursor: purging ? "wait" : "pointer",
                       fontFamily: "Manrope", fontSize: "11px", fontWeight: 600, letterSpacing: "0.05em",
                       textTransform: "uppercase" }}>
              Найти тестовых
            </button>
            <button data-testid="purge-test-btn" onClick={purgeTest} disabled={purging || (counts.test || 0) === 0}
              title="Удалить всех клиентов с меткой «ТЕСТ»"
              style={{ background: (counts.test || 0) === 0 ? "#f3f4f6" : "#FEF2F2",
                       color: (counts.test || 0) === 0 ? "#9ca3af" : "#B91C1C",
                       border: `1px solid ${(counts.test || 0) === 0 ? "#e5e7eb" : "#FECACA"}`,
                       borderRadius: "6px", padding: "8px 14px",
                       cursor: purging || (counts.test || 0) === 0 ? "not-allowed" : "pointer",
                       fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, letterSpacing: "0.05em",
                       textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: "5px" }}>
              <Trash2 size={12} /> Удалить тестовых ({counts.test || 0})
            </button>
          </>
        )}
      </div>

      {scanError && (
        <div data-testid="scan-error" style={{ marginBottom: "14px", padding: "10px 14px", background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: "6px", color: "#7F1D1D", fontFamily: "Manrope", fontSize: "13px" }}>
          {scanError}
        </div>
      )}

      {loading ? (
        <div style={{ fontFamily: "Manrope", color: "#888", padding: "32px", textAlign: "center" }}>Загрузка...</div>
      ) : (
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Manrope", minWidth: 900 }}>
            <thead>
              <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                <th style={{ padding: "12px 8px 12px 14px", width: "36px" }}>
                  {isAdmin && (
                    <input data-testid="select-all-visible" type="checkbox"
                      checked={allVisibleSelected}
                      ref={el => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                      onChange={toggleAllVisible}
                      style={{ accentColor: "#0A0A0A", width: "16px", height: "16px", cursor: "pointer" }}
                      onClick={e => e.stopPropagation()} />
                  )}
                </th>
                {["Клиент", "Регистрация", "Источник", "Телефон", "VIP", "Воронка CRM", "Менеджер", "Последний контакт", "Покупки", "Бонусы", ""].map(h => (
                  <th key={h} style={{ padding: "12px 14px", textAlign: "left", fontSize: "10.5px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.08em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const crm = c.crm_status || "new";
                const crmColor = CRM_STATUS_COLORS[crm] || "#94a3b8";
                const crmLabel = CRM_STATUS_LABELS[crm] || crm;
                const srcKey = c.client_source || "self_signup";
                const srcMeta = SOURCE_META[srcKey] || SOURCE_META.self_signup;
                const isChecked = selectedIds.has(c.id);
                return (
                <tr key={c.id} data-testid={`client-row-${c.id}`}
                  style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer", transition: "background 0.15s",
                            background: isChecked ? "#FFFBEB" : "transparent" }}
                  onClick={() => openClient(c.id)}
                  onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = "#fafafa"; }}
                  onMouseLeave={e => { if (!isChecked) e.currentTarget.style.background = "transparent"; }}>
                  <td style={{ padding: "12px 8px 12px 14px" }} onClick={e => e.stopPropagation()}>
                    {isAdmin && (
                      <input data-testid={`select-client-${c.id}`} type="checkbox" checked={isChecked}
                        onChange={() => toggleId(c.id)}
                        style={{ accentColor: "#0A0A0A", width: "16px", height: "16px", cursor: "pointer" }} />
                    )}
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <div style={{ fontWeight: 600, color: "#0A0A0A", fontSize: "13.5px" }}>{c.name || "—"}</div>
                    <div style={{ fontSize: "10.5px", color: "#888", marginTop: "2px" }}>{c.city || (c.crm_interested_in ? `↳ ${c.crm_interested_in}` : "")}</div>
                  </td>
                  <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                    {(() => {
                      if (!c.created_at) return <span style={{ fontSize: "11.5px", color: "#9ca3af" }}>—</span>;
                      const dt = new Date(c.created_at);
                      const isRecent = (Date.now() - dt.getTime()) < 7 * 24 * 3600 * 1000;
                      return (
                        <div>
                          <div style={{ fontSize: "11.5px", color: "#374151", fontWeight: isRecent ? 700 : 400 }}>
                            {dt.toLocaleDateString("ru-RU", { day: "numeric", month: "short", year: "numeric" })}
                          </div>
                          {isRecent && (
                            <span data-testid={`new-badge-${c.id}`}
                              style={{ background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0",
                                       fontSize: "9px", fontWeight: 800, padding: "1px 6px", borderRadius: "8px",
                                       letterSpacing: "0.08em" }}>
                              НОВЫЙ
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <span data-testid={`source-badge-${c.id}`}
                      style={{ background: srcMeta.bg, color: srcMeta.fg, border: `1px solid ${srcMeta.border}`,
                                fontWeight: 700, fontSize: "10px", padding: "3px 8px",
                                borderRadius: "10px", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
                      {srcMeta.label}
                    </span>
                    {c.app_registered && (
                      <div data-testid={`app-registered-${c.id}`}
                        title="Клиент зарегистрирован в мобильном приложении"
                        style={{ marginTop: 4, fontSize: 9, fontWeight: 800, letterSpacing: "0.08em",
                                  color: "#0A0A0A", background: "linear-gradient(135deg,#C5A059,#D4B878)",
                                  border: "1px solid #C5A059", padding: "2px 6px", borderRadius: 8,
                                  display: "inline-block", whiteSpace: "nowrap" }}>
                        В ПРИЛОЖЕНИИ
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: "12.5px", color: "#374151", whiteSpace: "nowrap" }}>{c.phone}</td>
                  <td style={{ padding: "12px 14px" }}>
                    <span style={{ background: `${colors[c.vip_level]}15`, color: colors[c.vip_level], fontWeight: 700, fontSize: "10.5px", padding: "3px 9px", borderRadius: "12px", letterSpacing: "0.08em" }}>{c.vip_level || "Standard"}</span>
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <span data-testid={`crm-status-${c.id}`} style={{ background: `${crmColor}18`, color: crmColor, fontWeight: 700, fontSize: "10.5px", padding: "3px 9px", borderRadius: "12px", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>{crmLabel}</span>
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: "12px", color: "#6b7280", whiteSpace: "nowrap" }}>{c.crm_manager_name || "—"}</td>
                  <td style={{ padding: "12px 14px", fontSize: "11.5px", color: "#6b7280", whiteSpace: "nowrap" }}>
                    {c.crm_last_contact_at ? new Date(c.crm_last_contact_at).toLocaleDateString("ru-RU", { day: "numeric", month: "short" }) : "—"}
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: "12.5px", color: "#0A0A0A", fontWeight: 600, whiteSpace: "nowrap" }}>{((c.total_purchases || 0) / 1000).toFixed(0)} K ₸</td>
                  <td style={{ padding: "12px 14px", fontSize: "12.5px", fontWeight: 600, whiteSpace: "nowrap" }}>
                    <div style={{ color: "#C5A059" }}>{Math.floor(c.bonus_balance || 0).toLocaleString("ru-RU")}</div>
                    {(c.locked_bonus || 0) > 0 && (
                      <div data-testid={`locked-hint-${c.id}`} style={{ fontSize: "10px", color: "#9ca3af", marginTop: "1px", fontWeight: 500 }}>
                        + {Math.floor(c.locked_bonus).toLocaleString("ru-RU")} замор.
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <ChevronRight size={14} color="#94a3b8" />
                  </td>
                </tr>);
              })}
            </tbody>
          </table>
          {filtered.length === 0 && <div style={{ padding: "40px", textAlign: "center", color: "#888", fontFamily: "Manrope" }}>Клиенты не найдены</div>}
        </div>
      )}

      {selected && <ClientDetail client={selected} onClose={() => setSelected(null)} onDeleted={() => load(search)} />}
      <QrScanner open={scannerOpen} onClose={() => setScannerOpen(false)} onResult={handleScannedClient} />

      {/* Sticky bulk-action bar — appears whenever ≥1 client is checked. */}
      {isAdmin && selectedIds.size > 0 && (
        <div data-testid="bulk-action-bar"
          style={{ position: "fixed", left: "50%", bottom: "24px", transform: "translateX(-50%)",
                    zIndex: 90, background: "#0A0A0A", color: "white",
                    borderRadius: "12px", padding: "12px 18px",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.35), 0 0 0 1px #C5A059",
                    display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap",
                    fontFamily: "Manrope" }}>
          <div style={{ fontSize: "13px", fontWeight: 600 }}>
            Выбрано: <span style={{ color: "#D4B878" }}>{selectedIds.size}</span>
          </div>
          <div style={{ width: "1px", height: "22px", background: "#333" }} />
          <button data-testid="bulk-mark-test-btn" onClick={() => bulkMarkTest(true)} disabled={bulkBusy}
            style={{ background: "transparent", color: "#FCA5A5", border: "1px solid #FCA5A5",
                      borderRadius: "6px", padding: "7px 12px", cursor: bulkBusy ? "wait" : "pointer",
                      fontSize: "11px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            Отметить тест
          </button>
          <button data-testid="bulk-unmark-test-btn" onClick={() => bulkMarkTest(false)} disabled={bulkBusy}
            style={{ background: "transparent", color: "#D1D5DB", border: "1px solid #4B5563",
                      borderRadius: "6px", padding: "7px 12px", cursor: bulkBusy ? "wait" : "pointer",
                      fontSize: "11px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            Снять метку
          </button>
          <button data-testid="bulk-delete-btn" onClick={bulkDelete} disabled={bulkBusy}
            style={{ background: "linear-gradient(135deg,#DC2626,#B91C1C)", color: "white", border: "none",
                      borderRadius: "6px", padding: "8px 14px", cursor: bulkBusy ? "wait" : "pointer",
                      fontSize: "11px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase",
                      display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <Trash2 size={13} /> {bulkBusy ? "Удаляем…" : "Удалить выбранных"}
          </button>
          <button data-testid="bulk-cancel-btn" onClick={clearSelection} disabled={bulkBusy}
            style={{ background: "transparent", color: "#9CA3AF", border: "none",
                      cursor: bulkBusy ? "wait" : "pointer", fontSize: "11px", padding: "6px 8px" }}>
            Отмена
          </button>
        </div>
      )}
    </div>
  );
}
